<script setup lang="ts">
import { computed } from 'vue'
import PlayerAvatar from '@/components/PlayerAvatar.vue'
import { useAuth } from '@/composables/auth'
import { T } from '@tolgee/vue'
import webapp from '@/services/webapp'
import ThePremium from '@/components/ThePremium.vue'
import WalletConnect from '@/components/WalletConnect.vue'
import PlayerBadges from '@/components/PlayerBadges.vue'
import PlayerAchievements from '@/components/PlayerAchievements.vue'
import { useAchievementsPage } from '@/composables/game/achievements'
import DropeeSpinnerIcon from '@/components/icons/DropeeSpinnerIcon.vue'
import PlayerInventory from '@/components/PlayerInventory.vue'
import { useInventory } from '@/composables/game/inventory'
import { useSnapshot } from '@/composables/game/snapshot'

const { profile } = useAuth()
const { achievements, isFetchingAchievements, categoryGroups, categoryTitleMap } = useAchievementsPage()
const { playerInventory } = useInventory()
const { isAfterSnapshot } = useSnapshot() 
</script>

<template>
  <div class="flex flex-col w-full items-center py-6 px-4 gap-y-10">
    <div class="flex flex-col items-center gap-y-6">
      <PlayerAvatar
        class="size-[88px] min-w-[88px]"
        :image-src="profile.photoUrl"
        :first-name="profile.firstName"
      />
      <div class="flex items-center gap-x-1">
        <div
          class="text-center text-light-white text-[32px] leading-[32px] font-bold bounce-in-right"
        >
          {{ profile.firstName }}
        </div>
        <ThePremium
          v-if="webapp.isPremium"
          image-class="size-5 min-w-[20px]"
        />
      </div>
    </div>
    <div
      v-if="playerInventory.length"
      class="w-full flex flex-col gap-y-4"
    >
      <div class="text-light-white font-semibold text-[22px] leading-7 -tracking-[0.44px]">
        <T
          key-name="inventory.title"
          default-value="Inventory"
        />
      </div>
      <PlayerInventory
        :inventory-items="playerInventory"
      />
    </div>
    <Transition
      name="fade"
      mode="out-in"
    >
      <div
        class="w-full h-[80vh] flex items-center justify-center"
        v-if="isFetchingAchievements"
      >
        <DropeeSpinnerIcon class="w-12 h-12 min-w-[48px] text-green-primary" />
      </div>
    </Transition>
    <div
      v-if="!isFetchingAchievements && achievements"
      class="w-full flex flex-col gap-y-3"
    >
      <div class="text-light-white font-semibold text-[22px] leading-7 -tracking-[0.44px]">
        <T
          key-name="achievements.title"
          default-value="Achievements"
        />
      </div>
      <PlayerAchievements
        :player-achievements="achievements"
        :category-groups
        :category-title-map
      />
    </div>
    <div
      class="w-full flex flex-col gap-y-4"
    >
      <div class="text-light-white font-semibold text-[22px] leading-7 -tracking-[0.44px]">
        <T
          key-name="badges.title"
          default-value="Badges"
        />
      </div>
      <PlayerBadges />
    </div>

    <div
      class="w-full flex flex-col gap-y-3"
    >
      <div class="text-light-white font-semibold text-[22px] leading-7 -tracking-[0.44px]">
        <T
          key-name="wallet.title"
          default-value="Wallet"
        />
      </div>
      <WalletConnect button-has-icon :disable-disconnect="isAfterSnapshot"/>
    </div>
  </div>
</template>

<style scoped>
.gradient-circle {
	background: linear-gradient(0deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.48) 100%)
}

.gray-border {
	background: linear-gradient(rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 0.4) 100%);
}
</style>
